import { parseRoute, type Route } from './route.js';
import { mountFloatingV2Toggle } from './render/v2-toggle.js';
import { createV2Prompt, shouldPrompt } from './render/v2-prompt.js';
import { renderHome } from './render/home.js';
import { renderRace } from './render/race.js';
import { renderOrg } from './render/org.js';
import { renderOrgLive } from './render/org-live.js';
import { renderCatalog } from './render/catalog.js';
import { renderAbout } from './render/about.js';
import { renderPrivacy } from './render/privacy.js';
import { renderOrgManager } from './org-manager/index.js';
import { renderCliApprove } from './render/cli-approve.js';
import { renderLinkGoogle } from './render/link-google.js';
import { initTheme } from './theme.js';
import { createNotFound } from './render/not-found.js';
import { handOffSignIn } from './hand-off.js';
import { chooseV2, isV2OnlyPath } from './v2-opt-in.js';

// Legendary hat keyframes are installed lazily on the first buildHatGroup
// call (see hat-svg.ts), so every entry point — main.ts, preview-race.ts,
// preview-finished.ts — gets animated legendaries automatically.

document.body.classList.add('tv'); // TV is the only mode
initTheme();

// Routes whose pages have no header of their own to hold the V2 switch.
const FLOATING = new Set<Route['type']>(['home', 'about', 'privacy', 'org-manager', 'cli', 'link', 'not-found']);

let activeCleanup: (() => void) | null = null;

function route() {
  const root = document.querySelector<HTMLElement>('#app');
  if (!root) return;

  if (activeCleanup) { activeCleanup(); activeCleanup = null; }

  handOffSignIn(window.location, (url) => history.replaceState(null, '', url));
  // Read before rendering: the org manager wipes a sign-in #code= as it starts.
  const at = { pathname: window.location.pathname, hash: window.location.hash };
  const r = parseRoute(at.pathname);
  if (r.type === 'home') {
    renderHome(root);
  } else if (r.type === 'race') {
    activeCleanup = renderRace(root, r.joinCode, { showGraphs: true });
  } else if (r.type === 'org') {
    activeCleanup = renderOrg(root, r.orgName);
  } else if (r.type === 'org-live') {
    activeCleanup = renderOrgLive(root, r.orgName);
  } else if (r.type === 'catalog') {
    activeCleanup = renderCatalog(root);
  } else if (r.type === 'about') {
    renderAbout(root);
  } else if (r.type === 'privacy') {
    renderPrivacy(root);
  } else if (r.type === 'org-manager') {
    activeCleanup = renderOrgManager(root);
  } else if (r.type === 'cli') {
    activeCleanup = renderCliApprove(root);
  } else if (r.type === 'link') {
    activeCleanup = renderLinkGoogle(root);
  } else {
    const v2Only = isV2OnlyPath(at.pathname);
    root.replaceChildren(createNotFound(document, v2Only ? {
      title: 'Only on the new site',
      message: ['This page is part of the new Token Derby.'],
      back: { label: '← Home', href: '/' },
      action: { label: 'Try v2', onClick: () => chooseV2(document) },
    } : {
      title: 'Page not found',
      message: ["There's nothing at this address."],
      back: { label: '← Home', href: '/' },
    }));
  }
  mountFloatingV2Toggle(document, FLOATING.has(r.type));
  if (!shouldPrompt(r.type, at)) document.querySelector('.v2-prompt')?.remove();
  else if (!document.querySelector('.v2-prompt')) document.body.appendChild(createV2Prompt(document));
}

window.addEventListener('popstate', route);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', route, { once: true });
} else {
  route();
}
